<script>
  import { fetchLogs, fetchLogsByEgresoCab, fetchLogsByDateRange } from "../controllers/logController";
  import { fetchEgresoCabs } from "../controllers/egresoCabController";
  import { fetchEgresoDets } from "../controllers/egresoDetController";
  import { onMount, onDestroy } from "svelte";
  import RightPane from "../components/Panes/RightPane.svelte";
  import pb from "../lib/pocketbase";
  import * as XLSX from 'xlsx';
  import { getEgresoPickers } from '../models/egresoCabModel.js';
  import { toasts } from 'svelte-toasts';

  // Two modes: either pass in egresoCab items or fetch them
  export let egresoCabItems = null; // Accept egresoCab items from parent
  export let egresoId = null; // Single egreso ID for backward compatibility
  
  let logs = {}; // Object to store logs by egresoCab ID
  let egresoCabs = []; // Array to store egresoCab items
  let isLoading = false; // Changed to false by default
  let error = null;
  let loadingEgresos = new Set();
  let failedEgresos = new Set();
  let isInitialLoad = true; // Track if this is the first load
  let userNames = new Map(); // Store user names
  let selectedDeliveryMethod = 'all';
  let uniqueDeliveryMethods = [];
  let egresoDetCounts = new Map(); // Store egresoDet line counts for each egresoCab

  // Right pane state
  let selectedItem = null;
  let showRightPane = false;

  // Search functionality
  let searchQuery = "";
  let filteredEgresoCabs = [];
  
  // Set default date range to yesterday and today
  let startDate = new Date();
  let endDate = new Date();
  startDate.setDate(startDate.getDate() - 1); // Set to yesterday by default
  startDate.setHours(0, 0, 0, 0); // Start of yesterday
  endDate.setHours(23, 59, 59, 999); // End of today

  // Replace ISO formatting for date inputs with local formatting
  function formatDateInputLocal(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  let formattedStartDate = formatDateInputLocal(startDate);
  let formattedEndDate = formatDateInputLocal(endDate);

  // Get today's date for max value of date inputs
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  const maxDate = today.toISOString().split('T')[0];

  // Format date for display
  function formatDate(dateString) {
    const date = new Date(dateString);
    
    // Get hours in 12-hour format
    let hours = date.getHours();
    const ampm = hours >= 12 ? 'p. m.' : 'a. m.';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }) + `, ${String(hours).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')} ${ampm}`;
  }

  // Format date for display
  function formatDisplayDate(date) {
    // Get hours in 12-hour format
    let hours = date.getHours();
    const ampm = hours >= 12 ? 'p. m.' : 'a. m.';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }) + `, ${String(hours).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')} ${ampm}`;
  }

  // Add this helper function to properly format dates for PocketBase
  function formatDateForPB(date) {
    if (!date) return null;
    const d = new Date(date);
    // Ensure we're working with a valid date
    if (isNaN(d.getTime())) return null;
    return d.toISOString();
  }

  // Modify loadData function to handle dates properly
  async function loadData() {
    isLoading = true;
    error = null;
    logs = {};
    loadingEgresos.clear();
    failedEgresos.clear();
    userNames.clear();

    try {
      // Validate dates first
      const validStartDate = new Date(startDate);
      const validEndDate = new Date(endDate);

      if (isNaN(validStartDate.getTime()) || isNaN(validEndDate.getTime())) {
        throw new Error('Fechas inválidas');
      }

      // Ensure start date is before end date
      if (validStartDate > validEndDate) {
        throw new Error('La fecha inicial debe ser anterior a la fecha final');
      }

      const logsResult = await fetchLogsByDateRange(validStartDate, validEndDate);
      logs = logsResult.logsByEgreso || {};

      if (egresoCabItems) {
        egresoCabs = Array.isArray(egresoCabItems) ? egresoCabItems : [];
      } else if (egresoId) {
        egresoCabs = [{ id: egresoId }];
      } else {
        const startDateStr = formatDateForPB(validStartDate);
        const endDateStr = formatDateForPB(validEndDate);
        
        if (!startDateStr || !endDateStr) {
          throw new Error('Error al formatear las fechas');
        }

        let filter = `created >= \"${startDateStr}\" && created <= \"${endDateStr}\"`;
        
        if (searchQuery && searchQuery.trim()) {
          const search = searchQuery.trim();
          // Escape special characters in the search query
          const safeSearch = search.replace(/[\\\"]/g, '\\$&');
          const searchFilter = `numero ~ \"${safeSearch}\" || nombre ~ \"${safeSearch}\" || observ ~ \"${safeSearch}\" || delivery_method ~ \"${safeSearch}\"`;
          filter = `(${filter}) && (${searchFilter})`;
        }
        
        try {
          const result = await pb.collection('egresoCab').getFullList({
            filter,
            sort: '-created',
            $autoCancel: false
          });
          egresoCabs = Array.isArray(result) ? result : [];
        } catch (err) {
          console.error("Error fetching egresoCabs:", err);
          showToast(`Error al cargar los egresos: ${err.message || 'Error de conexión'}`, 'error');
          egresoCabs = [];
        }
      }

      if (egresoCabs.length === 0) {
        showToast('No se encontraron registros para el rango de fechas seleccionado', 'info');
      }

      updateUniqueDeliveryMethods();
      filterEgresoCabs();
      
      // Load pickers for the initial page
      if (paginatedEgresoCabs.length > 0) {
        try {
          await loadPickersForEgresos(paginatedEgresoCabs);
        } catch (err) {
          console.error("Error loading pickers:", err);
          showToast(`Error al cargar los pickers: ${err.message || 'Error de conexión'}`, 'error');
        }
      }
      
      isInitialLoad = false;
    } catch (err) {
      error = err.message || "Error al cargar los datos";
      console.error("Error in loadData:", err);
      showToast(error, 'error');
      egresoCabs = [];
      filteredEgresoCabs = [];
    } finally {
      isLoading = false;
    }
  }

  // Update the date input handling
  $: {
    if (formattedStartDate && formattedEndDate) {
      try {
        const newStartDate = new Date(formattedStartDate);
        newStartDate.setHours(0, 0, 0, 0);
        
        const newEndDate = new Date(formattedEndDate);
        newEndDate.setHours(23, 59, 59, 999);

        if (!isNaN(newStartDate.getTime()) && !isNaN(newEndDate.getTime())) {
          startDate = newStartDate;
          endDate = newEndDate;
          if (!isInitialLoad) {
            loadData();
          }
        }
      } catch (err) {
        console.error("Error parsing dates:", err);
        showToast('Error al procesar las fechas', 'error');
      }
    }
  }

  onMount(() => {
    // Load initial data with default date range (yesterday to today)
    loadData();
  });

  // Helper to check if an egreso has logs
  function hasLogs(egresoId) {
    return logs[egresoId] && Array.isArray(logs[egresoId]) && logs[egresoId].length > 0;
  }

  // Reactive statement to watch search query changes
  $: if (searchQuery !== undefined) {
    filterEgresoCabs();
  }

  // Function to handle item click
  function handleItemClick(egreso, log) {
    selectedItem = {
      ...egreso,
      sta: log.new_status // Override the status with the current log's status
    };
    showRightPane = true;
  }

  // Function to close right pane
  function closeRightPane() {
    showRightPane = false;
    selectedItem = null;
  }

  // Function to calculate time between statuses and total time (in seconds)
  function calculateTimeBetweenStatuses(logs) {
    if (!logs || logs.length < 2) return { timeDiffs: [], totalTime: 0 };
    // Sort logs by created date ascending
    const sortedLogs = [...logs].sort((a, b) => new Date(a.created) - new Date(b.created));
    let start = null;
    let end = null;
    for (const log of sortedLogs) {
      if (log.new_status === 'NOT_READY' && !start) {
        start = new Date(log.created);
      }
      if (log.new_status === 'DELIVERED') {
        end = new Date(log.created);
      }
    }
    if (start && end) {
      const totalSeconds = Math.abs(Math.floor((end - start) / 1000));
      return { timeDiffs: [], totalTime: totalSeconds };
    }
    // fallback: use first and last log
    const first = new Date(sortedLogs[0].created);
    const last = new Date(sortedLogs[sortedLogs.length - 1].created);
    const totalSeconds = Math.abs(Math.floor((last - first) / 1000));
    return { timeDiffs: [], totalTime: totalSeconds };
  }

  let isExporting = false;

  // Add this function to load pickers in parallel
  async function loadPickersForEgresos(egresos) {
    const egresosNeedingPickers = egresos.filter(egreso => !pickersByEgreso[egreso.id]);
    
    if (egresosNeedingPickers.length === 0) return;

    try {
      // Load all pickers in parallel
      const results = await Promise.all(
        egresosNeedingPickers.map(egreso =>
          getEgresoPickers(egreso.id, 1, 100)
            .then(result => ({
              egresoId: egreso.id,
              pickerNames: result.items.map(item => item.expand?.picker?.name).filter(Boolean)
            }))
            .catch(error => {
              console.error("Error loading pickers for egreso:", egreso.id, error);
              return { egresoId: egreso.id, pickerNames: [] };
            })
        )
      );

      // Update pickersByEgreso with all results at once
      results.forEach(({ egresoId, pickerNames }) => {
        pickersByEgreso[egresoId] = pickerNames;
      });

      // Force Svelte reactivity
      pickersByEgreso = { ...pickersByEgreso };
    } catch (error) {
      console.error("Error in batch loading pickers:", error);
      throw error;
    }
  }

  // Replace showNotification function with this
  function showToast(message, type = 'success') {
    toasts.add({
      title: type === 'success' ? 'Éxito' : 'Error',
      description: message,
      duration: 3000,
      placement: 'bottom-right',
      type: type,
      theme: 'dark',
      showProgress: true
    });
  }

  async function exportToExcel() {
    isExporting = true;
    try {
      // Get all filtered egresoCabs with expanded pickers in a single query
      const startDateStr = formatDateForPB(startDate);
      const endDateStr = formatDateForPB(endDate);
      let filter = `created >= \"${startDateStr}\" && created <= \"${endDateStr}\"`;
      
      if (searchQuery && searchQuery.trim()) {
        const search = searchQuery.trim();
        const safeSearch = search.replace(/[\\\"]/g, '\\$&');
        const searchFilter = `numero ~ \"${safeSearch}\" || nombre ~ \"${safeSearch}\" || observ ~ \"${safeSearch}\" || delivery_method ~ \"${safeSearch}\"`;
        filter = `(${filter}) && (${searchFilter})`;
      }

      if (selectedDeliveryMethod !== 'all') {
        filter = `(${filter}) && delivery_method = \"${selectedDeliveryMethod}\"`;
      }

      // First get all egresoCabs
      const result = await pb.collection('egresoCab').getFullList({
        filter,
        sort: '-created'
      });

      // Load pickers for all egresoCabs
      await Promise.all(result.map(async (egreso) => {
        try {
          const pickersResult = await getEgresoPickers(egreso.id, 1, 100);
          pickersByEgreso[egreso.id] = pickersResult.items
            .map(item => item.expand?.picker?.name)
            .filter(Boolean);
        } catch (err) {
          console.error(`Error loading pickers for egreso ${egreso.id}:`, err);
          pickersByEgreso[egreso.id] = [];
        }
      }));

      // Fetch egresoDet counts for all egresos to be exported
      const egresoDetCountsExport = {};
      await Promise.all(result.map(async (egreso) => {
        try {
          const egresoDets = await fetchEgresoDets(egreso.id);
          egresoDetCountsExport[egreso.id] = egresoDets.length;
        } catch (err) {
          egresoDetCountsExport[egreso.id] = 0;
        }
      }));

      // Force Svelte reactivity
      pickersByEgreso = { ...pickersByEgreso };

      const wb = XLSX.utils.book_new();
      const wsData = [
        ['Número de Pedido', 'Nombre', 'Observaciones', 'Método de Entrega', 'Pickers', 'Estado', 'Fecha del Estado', 'Operador', 'Tiempo Total (Segundos)', 'Líneas de Detalle', 'Detalles de Estados']
      ];

      // Map the data including pickers from pickersByEgreso
      const rows = result.map(egreso => {
        const egresoLogs = logs[egreso.id] || [];
        const pickers = pickersByEgreso[egreso.id] ? pickersByEgreso[egreso.id].join(', ') : '';
        const { totalTime } = calculateTimeBetweenStatuses(egresoLogs);
        const detCount = egresoDetCountsExport[egreso.id] || 0;

        // Get unique statuses sorted by date
        const uniqueStatuses = egresoLogs.reduce((acc, log) => {
          if (!acc.some(item => item.new_status === log.new_status)) {
            acc.push(log);
          }
          return acc;
        }, []).sort((a, b) => new Date(a.created) - new Date(b.created));

        // Format status details showing first and last occurrence
        const statusDetails = uniqueStatuses.map(statusLog => {
          const statusLogs = egresoLogs.filter(l => l.new_status === statusLog.new_status)
            .sort((a, b) => new Date(a.created) - new Date(b.created));
          const firstLog = statusLogs[0];
          const lastLog = statusLogs[statusLogs.length - 1];
          const status = getStatusTranslation(statusLog.new_status);
          const firstTimestamp = formatDate(firstLog.created);
          const lastTimestamp = formatDate(lastLog.created);
          const firstUser = firstLog.changed_by || 'Sistema';
          const lastUser = lastLog.changed_by || 'Sistema';
          if (firstLog === lastLog) {
            return `${status} (${firstTimestamp} - ${firstUser})`;
          } else {
            return `${status} (${firstTimestamp} - ${firstUser} → ${lastTimestamp} - ${lastUser})`;
          }
        }).join('\n');

        // Get latest log for main columns
        const latestLog = egresoLogs.slice().sort((a, b) => new Date(b.created) - new Date(a.created))[0];

        return [
          egreso.numero || '',
          egreso.nombre || '',
          egreso.observ || '',
          egreso.delivery_method || '',
          pickers,
          latestLog ? getStatusTranslation(latestLog.new_status) : '',
          latestLog ? formatDate(latestLog.created) : '',
          latestLog ? (latestLog.changed_by || 'Sistema') : '',
          totalTime,
          detCount,
          statusDetails
        ];
      });

      wsData.push(...rows);
      const ws = XLSX.utils.aoa_to_sheet(wsData);

      const colWidths = [
        { wch: 15 },  // Número de Pedido
        { wch: 30 },  // Nombre
        { wch: 40 },  // Observaciones
        { wch: 20 },  // Método de Entrega
        { wch: 30 },  // Pickers
        { wch: 20 },  // Estado
        { wch: 25 },  // Fecha del Estado
        { wch: 20 },  // Operador
        { wch: 15 },  // Tiempo Total
        { wch: 15 },  // Líneas de Detalle
        { wch: 100 }  // Detalles de Estados
      ];
      ws['!cols'] = colWidths;

      XLSX.utils.book_append_sheet(wb, ws, 'Timeline');
      const currentDate = new Date().toISOString().split('T')[0];
      const filename = `timeline_export_${currentDate}.xlsx`;
      XLSX.writeFile(wb, filename);
      
      showToast('Archivo Excel generado exitosamente');
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      showToast('Error al generar el archivo Excel', 'error');
    } finally {
      isExporting = false;
    }
  }

  let isLoadingLogs = false;

  // Add pickersByEgreso state
  let pickersByEgreso = {};

  // Function to load logs for a page of egresos
  async function loadLogsForPage(egresos) {
    if (!egresos || egresos.length === 0) return;
    
    await Promise.all(egresos.map(async (egreso) => {
      // Always count egresoDet lines
      await countEgresoDetLines(egreso.id);
      if (!logs[egreso.id]) {
        loadingEgresos.add(egreso.id);
        try {
          const result = await pb.collection('logs').getList(1, 50, {
            filter: `record_id = "${egreso.id}" && collection = "egresoCab"`,
            sort: '-created',
            $autoCancel: false
          });
          logs[egreso.id] = result.items || [];
          logs = { ...logs }; // Force Svelte reactivity
          // Load pickers
          await loadPickersForEgresos([egreso]);
        } catch (e) {
          failedEgresos.add(egreso.id);
          console.error("Error fetching logs for egreso:", egreso.id, e);
        } finally {
          loadingEgresos.delete(egreso.id);
        }
      } else {
        // If logs already loaded, still load pickers for consistency
        await loadPickersForEgresos([egreso]);
      }
    }));
  }

  // After filtering and paginating egresoCabs, ensure logs are loaded
  $: if (paginatedEgresoCabs && paginatedEgresoCabs.length > 0) {
    loadLogsForPage(paginatedEgresoCabs);
  }

  // Add a reactive statement to update UI when logs change
  $: if (logs) {
    // Force component update when logs change
    paginatedEgresoCabs = [...paginatedEgresoCabs];
  }

  // Debounce search so loadData is only called after user stops typing
  let searchTimeout;
  $: if (searchQuery !== undefined || formattedStartDate !== undefined || formattedEndDate !== undefined) {
    if (searchTimeout) clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
      loadData();
    }, 400);
  }

  // Add these with other functions
  function updateUniqueDeliveryMethods() {
    const methods = new Set();
    methods.add('all'); // Add 'all' option
    egresoCabs.forEach(egreso => {
      if (egreso.delivery_method) {
        methods.add(egreso.delivery_method);
      }
    });
    uniqueDeliveryMethods = Array.from(methods);
  }

  // Status color mapping
  const statusColors = {
    NOT_READY: 'bg-red-200 border-red-300 text-red-700',
    PACKING: 'bg-yellow-500 border-yellow-600 text-yellow-900',
    PARTIALLY_READY: 'bg-orange-200 border-orange-300 text-orange-700',
    READY: 'bg-teal-200 border-teal-300 text-teal-700',
    ON_ROUTE: 'bg-lightBlue-200 border-lightBlue-300 text-lightBlue-700',
    DELIVERED: 'bg-emerald-200 border-emerald-300 text-emerald-700',
    RETURNED: 'bg-red-200 border-red-300 text-red-700',
    DEFAULT: 'bg-gray-100 border-gray-300 text-gray-700',
  };

  // Status translations
  const statusTranslations = {
    NOT_READY: 'No listo',
    PACKING: 'En empaque',
    PARTIALLY_READY: 'Parcialmente listo',
    READY: 'Listo',
    ON_ROUTE: 'En ruta',
    DELIVERED: 'Entregado',
    RETURNED: 'Devuelto',
    DEFAULT: 'Desconocido'
  };

  // Get the appropriate color class for a status
  function getStatusColorClass(status) {
    return statusColors[status] || statusColors.DEFAULT;
  }

  // Get the Spanish translation for a status
  function getStatusTranslation(status) {
    return statusTranslations[status] || statusTranslations.DEFAULT;
  }

  // Pagination variables
  let currentPage = 1;
  let itemsPerPage = 5; // Number of egresoCabs to show per page
  let totalPages = 1;
  let paginatedEgresoCabs = [];

  // Function to update pagination
  function updatePagination() {
    totalPages = Math.ceil(filteredEgresoCabs.length / itemsPerPage);
    
    // Make sure currentPage is valid
    if (currentPage < 1) currentPage = 1;
    if (currentPage > totalPages) currentPage = totalPages || 1;
    
    // Get current page items
    const startIndex = (currentPage - 1) * itemsPerPage;
    paginatedEgresoCabs = filteredEgresoCabs.slice(startIndex, startIndex + itemsPerPage);
  }

  // Change page function
  function changePage(newPage) {
    if (newPage >= 1 && newPage <= totalPages) {
      currentPage = newPage;
      updatePagination();
      // Load logs for the new page
      loadLogsForPage(paginatedEgresoCabs);
    }
  }

  // Add reactive statement for delivery method changes
  $: if (selectedDeliveryMethod !== undefined) {
    filterEgresoCabs();
  }

  // Modify the filterEgresoCabs function
  function filterEgresoCabs() {
    let filtered = egresoCabs;

    // First filter by delivery method if selected
    if (selectedDeliveryMethod !== 'all') {
      filtered = filtered.filter(egreso => egreso.delivery_method === selectedDeliveryMethod);
    }

    // Then apply search query filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(egreso => {
        if (egreso.numero?.toString().toLowerCase().includes(query)) return true;
        if (egreso.observ?.toLowerCase().includes(query)) return true;
        if (egreso.nombre?.toLowerCase().includes(query)) return true;
        if (egreso.delivery_method?.toLowerCase().includes(query)) return true;
        
        if (logs[egreso.id]) {
          return logs[egreso.id].some(log => 
            (log.new_status && log.new_status.toLowerCase().includes(query)) ||
            (log.changed_by && log.changed_by.toLowerCase().includes(query)) ||
            (log.collection && log.collection.toLowerCase().includes(query))
          );
        }
        return false;
      });
    }
    
    filteredEgresoCabs = filtered;
    currentPage = 1;
    updatePagination();
  }

  // Handler for search input
  function handleSearch() {
    filterEgresoCabs();
  }

  // Add this function to count egresoDet lines for an egreso
  async function countEgresoDetLines(egresoId) {
    try {
      const egresoDets = await fetchEgresoDets(egresoId);
      egresoDetCounts.set(egresoId, egresoDets.length);
      egresoDetCounts = new Map(egresoDetCounts); // Force Svelte reactivity
    } catch (error) {
      console.error("Error counting egresoDet lines:", error);
      egresoDetCounts.set(egresoId, 0);
      egresoDetCounts = new Map(egresoDetCounts);
    }
  }
</script>

<style>
  .timeline-container {
    padding: 20px;
    background-color: #f8f9fa;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  }

  .timeline {
    display: flex;
    position: relative;
    padding: 1rem 0;
  }

  /* Horizontal timeline styling */
  @media (min-width: 768px) {
    .timeline {
      flex-direction: row;
      overflow-x: auto;
      padding-bottom: 2rem;
    }

    .timeline-item {
      flex: 0 0 300px;
      margin-right: 2rem;
      position: relative;
    }

    .timeline-item::after {
      content: '';
      position: absolute;
      top: 50%;
      right: -1.5rem;
      width: 1rem;
      height: 2px;
      background: #c5c9d0;
    }

    .timeline-item:last-child::after {
      display: none;
    }

    .timeline::before {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      height: 2px;
      background: #c5c9d0;
      z-index: 0;
    }
  }

  /* Vertical timeline styling */
  @media (max-width: 767px) {
    .timeline {
      flex-direction: column;
      padding-left: 2rem;
    }

    .timeline-item {
      margin-bottom: 1.5rem;
      position: relative;
    }

    .timeline-item::before {
      content: '';
      position: absolute;
      left: -2rem;
      top: 0;
      bottom: 0;
      width: 2px;
      background: #c5c9d0;
    }

    .timeline-item::after {
      content: '';
      position: absolute;
      left: -2.35rem;
      top: 1rem;
      width: 0.75rem;
      height: 0.75rem;
      border-radius: 50%;
      background: #3b82f6;
      border: 2px solid white;
      z-index: 1;
    }
  }

  .timeline-item {
    padding: 1rem;
    border-radius: 8px;
    background: white;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }

  .timeline-item:hover {
    transform: translateY(-3px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .timeline-header {
    padding-bottom: 0.5rem;
    margin-bottom: 0.5rem;
    border-bottom: 1px solid #eee;
    font-weight: 600;
    color: #333;
  }

  .timeline-content p {
    margin: 0.4rem 0;
    color: #555;
    font-size: 0.95rem;
  }

  .status-badge {
    display: inline-block;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 600;
    margin-top: 0.5rem;
    border-width: 1px;
    border-style: solid;
  }

  .timestamp {
    font-size: 0.8rem;
    color: #777;
    margin-top: 0.75rem;
    display: block;
  }

  /* Pagination styles */
  .pagination {
    display: flex;
    justify-content: center;
    margin-top: 2rem;
    padding-top: 1rem;
    border-top: 1px solid #eee;
  }

  .pagination-btn {
    padding: 0.5rem 1rem;
    margin: 0 0.25rem;
    border-radius: 4px;
    font-size: 0.875rem;
    background: white;
    border: 1px solid #ddd;
    cursor: pointer;
    transition: all 0.2s;
  }

  .pagination-btn:hover {
    background: #f8f9fa;
    border-color: #c5c9d0;
  }

  .pagination-btn.active {
    background: #3b82f6;
    color: white;
    border-color: #3b82f6;
  }

  .pagination-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Search input styles */
  .search-container {
    margin-bottom: 1.5rem;
    position: relative;
  }

  .search-input {
    width: 100%;
    padding: 0.75rem 1rem 0.75rem 2.5rem;
    border: 1px solid #e2e8f0;
    border-radius: 0.375rem;
    font-size: 0.875rem;
    transition: border-color 0.15s ease;
  }

  .search-input:focus {
    border-color: #3b82f6;
    outline: none;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  .search-icon {
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: #a0aec0;
  }

  .search-button {
    position: absolute;
    right: 0.5rem;
    top: 50%;
    transform: translateY(-50%);
    padding: 0.25rem 0.75rem;
    background-color: #3b82f6;
    color: white;
    border-radius: 0.25rem;
    font-size: 0.75rem;
    font-weight: 600;
    transition: background-color 0.15s ease;
  }

  .search-button:hover {
    background-color: #2563eb;
  }

  .export-excel-btn {
    background-color: var(--color-blue-600, #2563eb);
  }
  .export-excel-btn:hover {
    background-color: var(--color-blue-700, #1d4ed8);
  }

  .picker-row {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 0.5rem;
  }

  .picker-badge {
    padding: 0.25rem 0.5rem;
    background-color: #e2e8f0;
    border-radius: 0.25rem;
    font-size: 0.75rem;
    color: #4a5568;
  }

  /* Add these styles for the toasts */
  :global(.svelte-toasts) {
    height: auto !important;
  }
  :global(.svelte-toast) {
    margin: 0.5em;
    padding: 0.75em 1em;
    border-radius: 0.25em;
    font-size: 0.875rem;
  }
  :global(.svelte-toast.success) {
    background: #10B981;
    color: white;
  }
  :global(.svelte-toast.error) {
    background: #EF4444;
    color: white;
  }
</style>

<div class="relative md:pt-6 pb-4 flex flex-col justify-between items-start">
    <div class="timeline-container w-full">
        <div class="flex justify-between items-center mb-4">
            <h2 class="text-xl font-semibold">Trazabilidad de pedidos</h2>
            <button 
                on:click={exportToExcel}
                class="export-excel-btn px-4 py-2 text-white rounded transition-colors"
                disabled={isExporting}
            >
                {#if isExporting}
                    <i class="fas fa-spinner fa-spin mr-2"></i>Preparando datos...
                {:else}
                    <i class="fas fa-file-excel mr-2"></i>Exportar a Excel
                {/if}
            </button>
        </div>
        <!-- Search input and date filters: always visible -->
        <div class="search-container">
            <div class="flex flex-col md:flex-row gap-4 mb-4">
                <div class="flex-1">
                    <label class="text-sm text-gray-600 mb-1">Buscar</label>
                    <div class="relative">
                        <span class="search-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </span>
                        <input 
                            type="text" 
                            bind:value={searchQuery} 
                            placeholder="Número, nombre, observaciones..."
                            class="search-input"
                        />
                        {#if searchQuery}
                            <button 
                                class="absolute right-16 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                on:click={() => { 
                                    searchQuery = ""; 
                                }}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        {/if}
                    </div>
                </div>
                <div class="flex gap-4">
                    <div class="flex flex-col">
                        <label class="text-sm text-gray-600 mb-1">Método de entrega</label>
                        <select 
                            bind:value={selectedDeliveryMethod}
                            class="border rounded px-3 py-2 text-sm"
                        >
                            {#each uniqueDeliveryMethods as method}
                                <option value={method}>
                                    {method === 'all' ? 'Todos' : method}
                                </option>
                            {/each}
                        </select>
                    </div>
                    <div class="flex flex-col">
                        <label class="text-sm text-gray-600 mb-1">Desde</label>
                        <input 
                            type="date" 
                            bind:value={formattedStartDate}
                            max={formattedEndDate}
                            class="border rounded px-3 py-2 text-sm"
                        />
                    </div>
                    <div class="flex flex-col">
                        <label class="text-sm text-gray-600 mb-1">Hasta</label>
                        <input 
                            type="date" 
                            bind:value={formattedEndDate}
                            min={formattedStartDate}
                            max={maxDate}
                            class="border rounded px-3 py-2 text-sm"
                        />
                    </div>
                </div>
            </div>
        </div>
        {#if isLoading}
            <div class="p-4 text-center">
                <p>Cargando logs...</p>
            </div>
        {:else if error}
            <div class="p-4 text-center text-red-500">
                <p>{error}</p>
            </div>
        {:else if egresoCabs.length === 0}
            <div class="p-4 text-center">
                <p>No se encontraron egresos para mostrar.</p>
            </div>
        {:else}
            <!-- Summary of results -->
            <div class="mb-4 text-sm text-gray-600">
                Mostrando {paginatedEgresoCabs.length} de {filteredEgresoCabs.length} egresos con logs
                {#if searchQuery}
                    <span>"{searchQuery}"</span>
                {/if}
            </div>
            
            <!-- Iterate through each egresoCab on current page -->
            {#each paginatedEgresoCabs || [] as egreso}
                <div class="mb-8">
                    <h3 class="text-lg font-semibold mb-3">
                        {egreso.nombre} 
                        {#if egreso.observ}
                            {egreso.observ}
                        {/if}
                        {#if loadingEgresos.has(egreso.id)}
                            <span class="text-sm text-gray-500 ml-2">(Cargando...)</span>
                        {:else if failedEgresos.has(egreso.id)}
                            <span class="text-sm text-red-500 ml-2">(Error al cargar)</span>
                        {/if}
                    </h3>
                    {#if loadingEgresos.has(egreso.id)}
                        <div class="p-4 text-center text-gray-500">Cargando...</div>
                    {:else if failedEgresos.has(egreso.id)}
                        <div class="p-4 text-center text-red-500">Error al cargar los estados.</div>
                    {:else if logs[egreso.id] && logs[egreso.id].length > 0}
                        <div class="text-sm text-gray-600 mb-2">
                            Tiempo total: {Math.abs(calculateTimeBetweenStatuses(logs[egreso.id]).totalTime)} segundos
                        </div>
                    {/if}
                    
                    <!-- Timeline for this egreso -->
                    {#if !loadingEgresos.has(egreso.id) && !failedEgresos.has(egreso.id)}
                        {#if logs[egreso.id] && logs[egreso.id].length > 0}
                            <div class="timeline">
                                {#each logs[egreso.id].reduce((acc, log) => {
                                    // Only add if this status hasn't been shown before
                                    if (!acc.some(item => item.new_status === log.new_status)) {
                                        acc.push(log);
                                    }
                                    return acc;
                                }, []).sort((a, b) => new Date(a.created) - new Date(b.created)) as log}
                                    <div class="timeline-item cursor-pointer hover:shadow-lg" on:click={() => handleItemClick(egreso, log)}>
                                        <div class="timeline-header">
                                            Pedido #{egreso.numero}
                                        </div>
                                        <div class="timeline-content">
                                            <p><strong>Método de entrega:</strong> {egreso.delivery_method || 'No especificado'}</p>
                                            <p><strong>Operado por:</strong> {log.changed_by}</p>
                                            <p><strong>Líneas de detalle:</strong> {egresoDetCounts.get(egreso.id) || 0}</p>
                                            {#if egreso.target_ref}
                                                <p><strong>Referencia:</strong> {egreso.target_ref}</p>
                                            {/if}
                                            {#if egreso.nombre_recibe}
                                                <p><strong>Recibe:</strong> {egreso.nombre_recibe}</p>
                                            {/if}
                                            {#if egreso.tel_recibe}
                                                <p><strong>Teléfono:</strong> {egreso.tel_recibe}</p>
                                            {/if}
                                            {#if pickersByEgreso[egreso.id] && pickersByEgreso[egreso.id].length > 0}
                                                <div class="picker-row">
                                                    {#each pickersByEgreso[egreso.id] as picker}
                                                        <span class="picker-badge">{picker}</span>
                                                    {/each}
                                                </div>
                                            {/if}
                                            <div class="status-badge {getStatusColorClass(log.new_status)}">
                                                {getStatusTranslation(log.new_status)}
                                            </div>
                                            <span class="timestamp">
                                                {formatDate(log.created)}
                                            </span>
                                        </div>
                                    </div>
                                {/each}
                            </div>
                        {:else}
                            <div class="p-4 text-center text-gray-500">
                                No hay estados para este egreso en el rango de fechas seleccionado.
                            </div>
                        {/if}
                    {:else if loadingEgresos.has(egreso.id)}
                        <div class="p-4 text-center text-gray-500">Cargando...</div>
                    {:else if failedEgresos.has(egreso.id)}
                        <div class="p-4 text-center text-red-500">Error al cargar los estados.</div>
                    {/if}
                </div>
            {/each}

            <!-- Show message if no logs found for any egreso -->
            {#if paginatedEgresoCabs.length === 0}
                <div class="p-4 text-center">
                    {#if searchQuery || startDate || endDate}
                        <p>No se encontraron egresos que coincidan con la búsqueda.</p>
                        <button 
                            class="mt-2 text-blue-500 hover:text-blue-700 underline"
                            on:click={() => { searchQuery = ""; startDate = new Date(); endDate = new Date(); filterEgresoCabs(); }}
                        >
                            Limpiar búsqueda
                        </button>
                    {/if}
                </div>
            {/if}

            <!-- Pagination controls -->
            {#if totalPages > 1}
                <div class="pagination">
                    <button 
                        class="pagination-btn" 
                        on:click={() => changePage(1)} 
                        disabled={currentPage === 1}>
                        &laquo;
                    </button>
                    
                    <button 
                        class="pagination-btn" 
                        on:click={() => changePage(currentPage - 1)} 
                        disabled={currentPage === 1}>
                        &lt;
                    </button>
                    
                    <!-- Show page numbers with ellipsis for many pages -->
                    {#if totalPages <= 7}
                        {#each Array(totalPages) as _, i}
                            <button 
                                class="pagination-btn {currentPage === i + 1 ? 'active' : ''}" 
                                on:click={() => changePage(i + 1)}>
                                {i + 1}
                            </button>
                        {/each}
                    {:else}
                        <!-- First page -->
                        <button 
                            class="pagination-btn {currentPage === 1 ? 'active' : ''}" 
                            on:click={() => changePage(1)}>
                            1
                        </button>
                        
                        <!-- Ellipsis if needed -->
                        {#if currentPage > 3}
                            <span class="pagination-btn">...</span>
                        {/if}
                        
                        <!-- Pages around current page -->
                        {#each Array(3) as _, i}
                            {#if currentPage - 1 + i > 1 && currentPage - 1 + i < totalPages}
                                <button 
                                    class="pagination-btn {currentPage === currentPage - 1 + i ? 'active' : ''}" 
                                    on:click={() => changePage(currentPage - 1 + i)}>
                                    {currentPage - 1 + i}
                                </button>
                            {/if}
                        {/each}
                        
                        <!-- Ellipsis if needed -->
                        {#if currentPage < totalPages - 2}
                            <span class="pagination-btn">...</span>
                        {/if}
                        
                        <!-- Last page -->
                        <button 
                            class="pagination-btn {currentPage === totalPages ? 'active' : ''}" 
                            on:click={() => changePage(totalPages)}>
                            {totalPages}
                        </button>
                    {/if}
                    
                    <button 
                        class="pagination-btn" 
                        on:click={() => changePage(currentPage + 1)} 
                        disabled={currentPage === totalPages}>
                        &gt;
                    </button>
                    
                    <button 
                        class="pagination-btn" 
                        on:click={() => changePage(totalPages)} 
                        disabled={currentPage === totalPages}>
                        &raquo;
                    </button>
                </div>
            {/if}
        {/if}
    </div>
</div>

<!-- Right Pane -->
{#if showRightPane && selectedItem}
    <RightPane 
        selectedItem={selectedItem} 
        closePane={closeRightPane}
        status={selectedItem.sta}
    />
{/if}